'use client';

import { type AriaAttributes, type ComponentProps, useEffect, useRef, useState } from 'react';
import { Avatar } from '@zillow/constellation-10';

type UserAvatarProps = Pick<ComponentProps<typeof Avatar>, 'size' | 'tone'> &
  AriaAttributes & {
    photoUrl?: string;
    initials: string;
  };

export function UserAvatar({ photoUrl, initials, ...avatarProps }: UserAvatarProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [failedUrl, setFailedUrl] = useState<string>();
  const showPhoto = Boolean(photoUrl) && photoUrl !== failedUrl;

  // React 18 doesn't replay error events fired before hydration, so a photo
  // that broke during the SSR paint never reaches onError — detect it on mount.
  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth === 0) {
      setFailedUrl(photoUrl);
    }
  }, [photoUrl]);

  return (
    <Avatar {...avatarProps}>
      {showPhoto ? (
        // alt="" — the photo is decorative; call sites label or hide the Avatar root
        <Avatar.Image
          ref={imageRef}
          src={photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailedUrl(photoUrl)}
        />
      ) : (
        initials
      )}
    </Avatar>
  );
}
